import { faH, faH1, faH2, faH3, faH4, faH5, faH6, faImage, faItalic, faLink, faListOl, faListUl } from "@fortawesome/pro-regular-svg-icons";
import { faBold } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link as ExtensionLink } from "@tiptap/extension-link";
import Placeholder from '@tiptap/extension-placeholder';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import parse from 'html-react-parser';
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageResize from 'tiptap-extension-resize-image';
import Button from "../components/Button";
import { MessageType } from "../helpers/types";
import { fileIcon } from "../helpers/utils";
import { useApplicationContext } from "../providers/ApplicationProvider";
import { useAuthContext } from "../providers/AuthProvider";

const ApplicationMessages = () => {
    const { apiCall } = useAuthContext();
    const { application, messages, setMessages } = useApplicationContext();
    const { t } = useTranslation();
    const messageContainerRef = useRef<HTMLDivElement | null>(null);
    const [content, setContent] = useState<string>('');
    const [isSending, setSending] = useState<boolean>(false);

    useEffect(() => {
        // Ensure the chat log is always scrolled to the bottom
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop =
                messageContainerRef.current.scrollHeight - messageContainerRef.current.clientHeight;
        }
    }, [application]);

    const handleContentChange = (reason: any) => {
        setContent(reason)
    }

    const reloadMessages = async () => {
        setSending(true);
        const response = await apiCall({
            action: 'getApplicationMessages',
            id: application?.id
        });

        if (response.data.status === 200) {
            setMessages(response.data.data)
            setSending(false);
        }
    }

    useEffect(() => {
        const element = messageContainerRef.current;
        if (element) {
            element.scrollTop = element.scrollHeight;
        }
    }, [messages]);

    const handleChange = (newContent: string) => {
        handleContentChange(newContent);
    }

    const editor = useEditor({
        extensions: [
            ImageResize,
            StarterKit,
            ExtensionLink.configure({
                openOnClick: false,
                autolink: true,
                defaultProtocol: 'https',
            }),
            Placeholder.configure({
                placeholder: 'Melding',
            }),
        ],
        content,
        editorProps: {
            attributes: {
                class: "form-control rounded-top-0 border-top-0",
                style: "min-height: 140px;",
            },
        },
        onUpdate: ({ editor }) => {
            handleChange(editor.getHTML());
        }
    })

    const sendMessage = async (value: string) => {
        if (value.length === 0) return;
        setSending(true);
        editor?.commands.clearContent();

        try {
            const response = await apiCall({
                action: 'addApplicationMessage',
                application: application?.id,
                message: value,
                filer_vedlegg: []
            });

            if (response.data.status === 200) {
                // setFiles([]);
                setContent('');
                reloadMessages();
            }
        } catch (error) {
            console.error("SendMessage ERROR: ", error);
        } finally {
            setSending(false);
        }
    }

    return (
        <>
            <div className="chat-content container" ref={messageContainerRef}>
                {messages?.map((message, index) => (
                    <Message key={index} {...message} />
                ))}
            </div>
            <div className="container mt-4">
                <Toolbar editor={editor} />
                <EditorContent editor={editor} />
                <div className="d-flex justify-content-end mt-4">
                    <Button
                        title="Send"
                        type="primary"
                        rightIcon="paper-plane-top"
                        onClick={() => sendMessage(content)}
                        loading={isSending}
                        loadingText={t('send_loading')}
                    />
                </div>
            </div>
        </>
    );
}

const Message = (props: MessageType) => {
    const { apiCall, user } = useAuthContext();
    const { markMessageAsRead } = useApplicationContext();
    const { id, from_name, from_id, application, message, sent, read_by_user, from_photo, attachments } = props;
    const isMe = user?.id === from_id;

    const markMessageRead = async () => {
        const response = await apiCall({
            action: 'markApplicationMessageRead',
            message: id,
            user: user?.id
        });
        if (response.data.status === 200) {
            markMessageAsRead(id);
        }
    }


    const updateImgSrc = (htmlString: string) => {
        return htmlString.replace(/<img\s+src="([^"]+)"/g, (_, src) => {
            const newSrc = src.startsWith('http') ? src : `${import.meta.env.VITE_ADMIN_API_URL}${src}`;
            return `<img src="${newSrc}"`;
        });
    };

    useEffect(() => {
        if (user && !read_by_user) {
            markMessageRead();
        }
    }, []);

    return (
        isMe ? (
            <div className="row justify-content-end mb-3">
                <div className="col-10 col-lg-6">
                    <div className="card incoming-message bg-primary-subtle">
                        <div className="card-body">
                            {parse(updateImgSrc(message))}
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className={`row mb-3 ${isMe && 'justify-content-end'}`}>
                <div className="col-10 col-lg-6">
                    <div className="d-flex align-items-start outgoing-message text-decoration-none">
                        <ProfileAvatar
                            name={from_name}
                            photo={from_photo}
                        />
                        <div className="ms-2">
                            <div className="d-flex justify-content-between mb-1">
                                <small className="text-muted">
                                    <strong>#{application}</strong> {formatDistanceToNow(sent, { addSuffix: true, locale: nb })} av {from_name}
                                </small>
                            </div>
                            <div className="card card-message-comment bg-light">
                                <div className="card-body">
                                    {parse(updateImgSrc(message))}

                                    {attachments !== null && attachments.length > 0 ? (
                                        attachments.map((attachment, index) => (
                                            <div key={index}>
                                                <a className="col-10 col-lg-5 text-primary text-decoration-none mt-3 mb-2 my-lg-0" href={import.meta.env.VITE_API_BASE_URL + attachment.download_url}>
                                                    <FontAwesomeIcon icon={fileIcon(attachment.name.split('.').pop() ?? '')} className="me-2" />
                                                    <span>{attachment.name}</span>
                                                </a>
                                            </div>
                                        ))
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    )
}

const Toolbar = ({ editor }: { editor: Editor | null }) => {
    if (!editor) return null;
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const selectHeading = (level: any) => {
        editor.chain().focus().toggleHeading({ level }).run();
        setIsDropdownOpen(false);
    };
    const getActiveHeadingLevel = () => {
        for (let level = 1; level <= 6; level++) {
            if (editor.isActive('heading', { level })) {
                return level;
            }
        }
        return null;
    };
    const renderActiveHeadingIcon = () => {
        const activeLevel = getActiveHeadingLevel();
        switch (activeLevel) {
            case 1:
                return <FontAwesomeIcon icon={faH1} className="button-item" />;
            case 2:
                return <FontAwesomeIcon icon={faH2} className="button-item" />;
            case 3:
                return <FontAwesomeIcon icon={faH3} className="button-item" />;
            case 4:
                return <FontAwesomeIcon icon={faH4} className="button-item" />;
            case 5:
                return <FontAwesomeIcon icon={faH5} className="button-item" />;
            case 6:
                return <FontAwesomeIcon icon={faH6} className="button-item" />;
            default:
                return <FontAwesomeIcon icon={faH} className="button-item" />;
        }
    };

    /* Handle image upload */
    const handleImageClick = () => {
        if (imageInputRef.current) {
            imageInputRef.current.click();
        }
    };
    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = (event.target.files && event.target.files[0]) ?? null;
        if (file) {
            addImageFromFile(file);
        }
    }
    const addImageFromFile = useCallback((file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const url = reader.result;

            // Ensure the result is a string
            if (typeof url === 'string') {
                editor.chain().focus().setImage({ src: url }).run();
            } else {
                console.error('Failed to load image: result is not a string');
            }
        };
        reader.readAsDataURL(file);
    }, [editor]);

    /* Handle link insert */
    const setLink = useCallback(() => {
        const previousUrl = editor.getAttributes('link').href
        const url = window.prompt('URL', previousUrl)

        // cancelled
        if (url === null) {
            return
        }

        // empty
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink()
                .run()

            return
        }

        // update link
        editor.chain().focus().extendMarkRange('link').setLink({ href: url })
            .run()
    }, [editor])

    return (
        <div className="bg-light rounded-top-2 form-control border-bottom-0 rounded-bottom-0 d-flex gap-2">
            <div className="dropdown">
                <button
                    className={`tiptap-toolbar-button ${editor.isActive("heading") ? "bg-dark text-white" : "text-dark"} dropdown-toggle dropdown-toggle-no-caret`}
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="inside"
                    aria-expanded="false"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    {renderActiveHeadingIcon()}
                </button>
                {isDropdownOpen && (
                    <ul className="dropdown-menu show" style={{ position: 'absolute', top: '100%', left: 0 }}>
                        {[1, 2, 3, 4, 5, 6].map((level) => (
                            <button
                                key={level}
                                onClick={(e) => {
                                    e.preventDefault();
                                    selectHeading(level);
                                }}
                                className={`dropdown-item ${editor.isActive('heading', { level })
                                    ? 'text-primary'
                                    : 'text-black'
                                    }`}
                            >
                                Heading {level}
                            </button>
                        ))}
                    </ul>
                )}
            </div>
            <button
                onClick={() => editor.chain().focus().toggleBold().run()}
                className={`tiptap-toolbar-button ${editor.isActive("bold") ? "bg-dark text-white" : "text-dark"}`}
            >
                <FontAwesomeIcon icon={faBold} className="button-item" />
            </button>
            <button
                onClick={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleItalic().run()
                }}
                className={`tiptap-toolbar-button ${editor.isActive("italic") ? "bg-dark text-white" : "text-dark"}`}
            >
                <FontAwesomeIcon icon={faItalic} className="button-item" />
            </button>

            <button
                onClick={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleBulletList().run()
                }}
                className={`tiptap-toolbar-button ms-3 ${editor.isActive("bulletList") ? "bg-dark text-white" : "text-dark"}`}
            >
                <FontAwesomeIcon icon={faListUl} className="button-item" />
            </button>
            <button
                onClick={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleOrderedList().run();
                }}
                className={`tiptap-toolbar-button ${editor.isActive("orderedList") ? "bg-dark text-white" : "text-dark"}`}
            >
                <FontAwesomeIcon icon={faListOl} className="button-item" />
            </button>

            <button
                onClick={handleImageClick}
                className={`tiptap-toolbar-button ms-3 text-dark`}
            >
                <FontAwesomeIcon icon={faImage} className="button-item" />
            </button>
            <input
                ref={imageInputRef}
                type="file"
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleImageSelect}
            />
            <button
                onClick={setLink}
                className={`tiptap-toolbar-button text-dark`}
            >
                <FontAwesomeIcon icon={faLink} className="button-item" />
            </button>
        </div>
    )
}

export const ProfileAvatar = (props: { name: string, photo: string }) => {
    const { name, photo } = props;

    const getInitials = () => {
        var parts = name.split(' ')
        var initials = ''
        for (var i = 0; i < parts.length; i++) {
            if (parts[i].length > 0 && parts[i] !== '' && i == 0 || i == parts.length - 1) {
                initials += parts[i][0]
            }
        }
        return initials.toUpperCase();
    }

    return (
        <div className="profile-box">
            {photo ? (
                <img src={photo} className="h-100" style={{ objectFit: 'cover' }} />
            ) : (
                <span className="small initials">{getInitials()}</span>
            )}
        </div>

    )
}

export default ApplicationMessages;